/* Every exercise gets its video container (his 2026-09-18 night ask). The picture comes from the
 * first of: a stored `thumbnail_url` · a YouTube link's own still · an uploaded MP4/MOV, whose
 * first frame the browser can draw · nothing, which is the plate tile. Pure; executed by
 * `videoThumb.test.ts`. */
export type Thumb =
  | { kind: "image"; src: string }
  | { kind: "video"; src: string }
  /* 🔴 R17 — PLAYABLE MEDIA WE ARE NOT ALLOWED TO PLAY. A direct `.mp4` on an origin the
     page's `media-src` does not list goes into `<video src>` and dies with nothing red: no error
     on screen, no fallback, just a player that never starts. Exactly the class the 2026-09-18 CSP
     fix closed for fonts and YouTube stills, one element over.
     ⚠️ THE HEADER IS NOT WIDENED, AND THAT IS DECIDED. `media-src https:` would buy a case the
     live catalogue never hits — everything is YouTube or lands in the bucket — at the cost of a
     real security boundary on his surface. The app decides by ORIGIN instead and offers the thing
     it can honestly offer: a link. */
  | { kind: "link"; src: string }
  | { kind: "none" };

/**
 * The origins a `<video src>` on this page may actually load from.
 *
 * 🔴 THIS LIST IS THE `media-src` DIRECTIVE, AND THE RULER IN `videoThumb.test.ts` READS
 * `netlify.toml` AND COMPARES THEM. Two statements of one security boundary is the whole bug
 * class: widen the header without touching this and the app keeps refusing what the page allows;
 * narrow the header without touching this and the app hands the browser a URL it will drop on the
 * floor. Neither failure shows up anywhere.
 *
 * ⚠️ `'self'` AND `blob:` ARE NOT ORIGINS AND ARE HANDLED SEPARATELY — a same-origin URL is any
 * relative path or one on `window.location.origin`, and a `blob:` URL has no origin at all.
 */
export const PLAYABLE_MEDIA_ORIGINS: readonly string[] = ["https://xtjbfaukkcfbdaadlldl.supabase.co"];

/**
 * Can the page actually PLAY this, or can it only link to it?
 *
 * ⚠️ A URL THAT DOES NOT PARSE IS TREATED AS RELATIVE, hence same-origin, hence playable — that
 * is what a bare `/media/x.mp4` is, and it is the one shape the bucket's own signed paths take.
 */
export function isPlayableHere(url: string, origin: string): boolean {
  if (url.startsWith("blob:")) return true;
  let u: URL;
  try { u = new URL(url, origin); } catch { return true; }
  if (u.origin === origin) return true;
  return PLAYABLE_MEDIA_ORIGINS.includes(u.origin);
}

export function youtubeId(url: string): string | null {
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\.|^m\./, "");
    if (host === "youtu.be") { const id = u.pathname.slice(1).split("/")[0] ?? ""; return id === "" ? null : id; }
    if (host === "youtube.com" || host === "youtube-nocookie.com") {
      if (u.pathname === "/watch") return u.searchParams.get("v");
      const m = u.pathname.match(/^\/(?:embed|shorts|v)\/([^/?]+)/);
      return m?.[1] ?? null;
    }
  } catch { /* not a URL */ }
  return null;
}

export function thumbFor(
  demoVideoUrl: string | null,
  thumbnailUrl: string | null,
  /* ⚠️ THE PAGE'S OWN ORIGIN IS PASSED IN, never read from `window` here. This module is pure
     and its test drives three origins; reaching for `window.location` would make the rule depend
     on where it happens to be called from, which is how a check passes in a test and fails on the
     deployed host. It defaults so no caller has to care. */
  origin: string = typeof window === "undefined" ? "" : window.location.origin,
): Thumb {
  if (thumbnailUrl !== null && thumbnailUrl !== "") return { kind: "image", src: thumbnailUrl };
  if (demoVideoUrl === null || demoVideoUrl === "") return { kind: "none" };
  const yt = youtubeId(demoVideoUrl);
  /* 🔴 `mqdefault`, NOT `hqdefault` — HIS EYE, 2026-09-20: *"why not all containers … the ones
     without a videos are different a bit"*. He was looking at a real difference and had the sign
     reversed: the EMPTY tiles were the correct shape and the ones with pictures were not.
     `hqdefault.jpg` is 480 × 360 — FOUR BY THREE — with the 16 / 9 frame LETTERBOXED inside it,
     so the black bars are part of the image rather than of our layout. `object-fit: cover` then
     crops a box that already contains bars, and no CSS can remove them because they are pixels.
     `mqdefault.jpg` is 320 × 180: true 16 / 9, no bars. At the 140 px these tiles are drawn it is
     four times the resolution needed, and it is a SMALLER file than the one it replaces — which
     matters today of all days.
     ⚠️ AND IT IS THE ONE URL BUILDER FOR EVERY SURFACE, so the exercises page's cards, the
     builder's rail and the row thumbnails all stop carrying bars together. */
  if (yt !== null) return { kind: "image", src: `https://i.ytimg.com/vi/${yt}/mqdefault.jpg` };
  if (/\.(mp4|mov|m4v|webm)(\?|#|$)/i.test(demoVideoUrl)) {
    /* 🔴 R17. The extension says it is playable media; the ORIGIN says whether this page may
       play it. Offering a link is the honest answer — the coach's video is real and reachable,
       just not from inside our document. */
    return isPlayableHere(demoVideoUrl, origin)
      ? { kind: "video", src: demoVideoUrl }
      : { kind: "link", src: demoVideoUrl };
  }
  return { kind: "none" };
}

/**
 * What to tell a coach about the link they just pasted — or `null` when there is nothing to say.
 *
 * 🔴 SAID AT THE PASTE, NOT DISCOVERED LATER. R17's defect is a player that never starts, with no
 * error anywhere a coach would look; the repair is not only to stop drawing the dead player but to
 * say, at the moment they can still do something about it, WHY and WHAT INSTEAD. A screen that
 * silently degrades is the same shape as a dead button with words on it.
 *
 * ⚠️ IT IS NOT A REFUSAL. The link is stored exactly as typed and the exercise is fine — a coach's
 * own video on their own host is a legitimate thing to reference, and the app offers it as a link.
 * Refusing the paste would be the page's limitation becoming the coach's problem.
 */
export function pastedLinkNote(url: string, origin: string): string | null {
  if (youtubeId(url) !== null) return null;
  if (!/\.(mp4|mov|m4v|webm)(\?|#|$)/i.test(url)) return null;
  if (isPlayableHere(url, origin)) return null;
  return "Saved. This one opens in a new tab rather than playing here \u2014 upload the file to play "
    + "it in the page.";
}
